import type { DriverInput, KartState } from "./types";
import { DEFAULT_RACE_CLASS, getRaceClassProfile, type RaceClass } from "./balance";
import { getKartWeightProfile } from "../kartBuild";

/** The planar drag constants shared by every vehicle simulation. */
export const ROLLING_DRAG = 2.4;
export const AERO_DRAG = 0.008;

/** Surface modifiers supplied by the caller that owns track projection. */
export interface VehicleDynamicsOptions {
  /** 0 on the road, 1 at the outside edge of the driveable loose band. */
  readonly offRoadRatio?: number;
  /** Distance beyond the physical road edge before the loose-surface band. */
  readonly startOutside?: number;
  /** Signed lateral projection at the beginning of this planar step. */
  readonly lateral?: number;
  /** Signature-zone speed multiplier; 1 means no special zone. */
  readonly signatureSpeedScale?: number;
  /** Signature-zone grip multiplier; 1 means no special zone. */
  readonly signatureGripScale?: number;
  /** Signature-zone steering multiplier; 1 means no special zone. */
  readonly signatureSteeringScale?: number;
  readonly raceClass?: RaceClass;
  readonly paceAssist?: number;
  readonly steeringScale?: number;
  /** Arena manoeuvring: tighter low-speed turns and travel-signed reverse steering. */
  readonly battleHandling?: boolean;
}

/**
 * Return the mini-turbo tier for a charged drift.
 *
 * This lives with the planar dynamics so Battle and race code can share the
 * exact same release thresholds without importing a track adapter.
 */
export function getDriftTier(charge: number): 0 | 1 | 2 | 3 {
  return charge >= 0.84 ? 3 : charge >= 0.58 ? 2 : charge >= 0.28 ? 1 : 0;
}

function clamp(value: number, low: number, high: number): number {
  return Math.max(low, Math.min(high, value));
}

function wrapAngle(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}

/**
 * Advance one kart through the shared, track-independent planar handling
 * model. Track adapters must project the kart before calling this helper and
 * pass the resulting surface values through `options`; this function never
 * queries a track, invents a centerline, or performs recovery/projection.
 *
 * The arithmetic intentionally mirrors the former in-line segment in
 * `stepKart`, including operation order, so moving the code does not retune
 * the established race model.
 */
export function stepVehicleDynamics(kart: KartState, input: DriverInput, dt: number, options: VehicleDynamicsOptions = {}): void {
  const tuning = getRaceClassProfile(options.raceClass ?? DEFAULT_RACE_CLASS);
  const build = getKartWeightProfile(kart.weightClass);
  const paceAssist = clamp(options.paceAssist ?? 0, -0.08, 0.12);
  const steeringScale = clamp(options.steeringScale ?? 1, 0.75, 1.35);
  const offRoadRatio = clamp(options.offRoadRatio ?? 0, 0, 1);
  const startOutside = Math.max(0, options.startOutside ?? 0);
  const lateral = options.lateral ?? 0;
  const signatureSpeedScale = options.signatureSpeedScale ?? 1;
  const signatureGripScale = options.signatureGripScale ?? 1;
  const signatureSteeringScale = options.signatureSteeringScale ?? 1;

  const MAX_SPEED = tuning.maxSpeed * build.maxSpeed * (1 + paceAssist);
  const BOOST_SPEED = tuning.boostSpeed * build.boostSpeed * (1 + paceAssist);
  const DRAFT_SPEED = tuning.maxSpeed * build.maxSpeed * (1.1 + paceAssist);
  const ACCELERATION = tuning.acceleration * build.acceleration;
  const BRAKING = tuning.braking * build.braking;
  const STEER_RATE = tuning.steerRate * build.steering;

  const forwardX = Math.sin(kart.yaw);
  const forwardZ = Math.cos(kart.yaw);
  const rightX = forwardZ;
  const rightZ = -forwardX;
  let longitudinal = kart.velocityX * forwardX + kart.velocityZ * forwardZ;
  let lateralVelocity = kart.velocityX * rightX + kart.velocityZ * rightZ;
  const speedRatio = Math.min(1, Math.abs(longitudinal) / MAX_SPEED);
  const enteringDrift = offRoadRatio < 0.08 && input.drift && speedRatio > 0.28 && Math.abs(input.steer) > 0.18 && kart.driftDirection === 0;
  const continuingDrift = offRoadRatio < 0.18 && input.drift && speedRatio > 0.24 && kart.driftDirection !== 0;
  const drifting = enteringDrift || continuingDrift;

  longitudinal += (input.throttle * ACCELERATION * (kart.draftBoostTime > 0 ? 1.22 : 1) * (1 - offRoadRatio * 0.42) - input.brake * BRAKING) * dt;
  const drag = (ROLLING_DRAG + longitudinal * longitudinal * AERO_DRAG) * Math.sign(longitudinal);
  longitudinal -= drag * dt;
  longitudinal *= Math.exp(-offRoadRatio * 1.9 * build.offRoadDrag * dt);
  const powered = kart.boostTime > 0 || kart.draftBoostTime > 0;
  const offRoadCap = powered ? 1 - offRoadRatio * 0.24 * build.offRoadDrag : 1 - offRoadRatio * 0.48 * build.offRoadDrag;
  const speedCap = (kart.boostTime > 0 ? BOOST_SPEED : kart.draftBoostTime > 0 ? DRAFT_SPEED : MAX_SPEED) * signatureSpeedScale;
  longitudinal = Math.max(-8 * (1 - offRoadRatio * 0.35), Math.min(speedCap * offRoadCap, longitudinal));

  const assistedSteering = steeringScale > 1.1;
  const committedSteer = drifting && kart.driftDirection !== 0 && !assistedSteering ? input.steer * 0.56 + kart.driftDirection * 0.3 : input.steer;
  const steeringTowardRoad = startOutside > 0 && input.steer * Math.sign(lateral) < -0.05;
  const looseSteering = steeringTowardRoad ? 1 + offRoadRatio * 1.1 : 1 - offRoadRatio * 0.16;
  const baseAuthority = options.battleHandling ? 0.78 + speedRatio * 0.22 : 0.24 + speedRatio * 0.76;
  // Smooth through rest: no stationary pivot or sign chatter while changing gear.
  const travelDirection = options.battleHandling ? clamp(longitudinal / 1.5, -1, 1) : 1;
  const steerAuthority = baseAuthority * (drifting ? (assistedSteering ? 1.18 : 0.9) : 1) * looseSteering * signatureSteeringScale;
  kart.yaw = wrapAngle(kart.yaw + committedSteer * STEER_RATE * steeringScale * steerAuthority * travelDirection * dt);
  if (steeringTowardRoad) {
    // The loose shoulder should punish speed, not trap a player beside the
    // course. Add tyre bite only while they deliberately steer back inward.
    lateralVelocity -= Math.sign(lateral) * offRoadRatio * 8 * build.steering * dt;
  }
  const grip = (drifting ? 1.65 : 8.6 - offRoadRatio * 5.1) * build.grip * signatureGripScale;
  lateralVelocity *= Math.exp(-grip * dt);

  if (drifting) {
    if (kart.driftDirection === 0) kart.driftDirection = input.steer < 0 ? -1 : 1;
    const counterSteering = input.steer * kart.driftDirection < -0.08;
    // Holding the drift line is useful, but deliberately balancing against the
    // slide is the mastery input. It charges the next tier faster without
    // changing the kart's steering authority or giving AI a separate rule.
    const chargeRate = (0.3 + speedRatio * 0.55) * tuning.driftChargeScale * build.driftCharge * (counterSteering ? 1.22 : 1);
    kart.driftCharge = Math.min(1, kart.driftCharge + dt * chargeRate);
    const driftSteer = assistedSteering ? input.steer * 0.68 + kart.driftDirection * 0.32 : input.steer * 0.45 + kart.driftDirection * 0.25;
    lateralVelocity += driftSteer * (4.5 + speedRatio * 3.4) * dt;
  } else {
    const tier = getDriftTier(kart.driftCharge);
    const controlledRelease = offRoadRatio < 0.65 && !kart.airborne;
    if (kart.driftDirection !== 0 && tier > 0 && kart.boostTime <= 0 && controlledRelease) {
      kart.boostTime = (tier === 3 ? 1.45 : tier === 2 ? 0.95 : 0.55) * build.miniTurboDuration;
      // Releasing a charged drift creates a short, readable skill window in
      // which a homing keg glances off instead of forcing a spin.
      kart.itemDodgeTime = Math.max(kart.itemDodgeTime, 0.34 + tier * 0.04);
      longitudinal = Math.max(longitudinal, MAX_SPEED * (0.82 + kart.driftCharge * 0.18));
    }
    kart.driftCharge = Math.max(0, kart.driftCharge - dt * 2.8);
    kart.driftDirection = 0;
  }

  const newForwardX = Math.sin(kart.yaw);
  const newForwardZ = Math.cos(kart.yaw);
  const newRightX = newForwardZ;
  const newRightZ = -newForwardX;
  kart.velocityX = newForwardX * longitudinal + newRightX * lateralVelocity;
  kart.velocityZ = newForwardZ * longitudinal + newRightZ * lateralVelocity;
  kart.x += kart.velocityX * dt;
  kart.z += kart.velocityZ * dt;
  kart.boostTime = Math.max(0, kart.boostTime - dt);
  kart.draftBoostTime = Math.max(0, kart.draftBoostTime - dt);
  kart.draftCooldown = Math.max(0, kart.draftCooldown - dt);
  kart.rampCooldown = Math.max(0, kart.rampCooldown - dt);
}
